import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ChangeEvent, type CSSProperties, type RefObject, type ButtonHTMLAttributes, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type SelectHTMLAttributes } from "react";
import { createPortal } from "react-dom";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { LogoMark } from "./Logo.js";
import { Select, TextField, useMediaQuery } from "./controls.js";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

/** True when the viewer allows motion (and we're in a real browser). jsdom/SSR and reduced motion -> false. */
export function motionAllowed(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Mount/unmount with CSS transitions instead of keyframes, so open/close is interruptible (reopening mid-exit
 * reverses from where it is). Render while `present` and spread `presenceAttrs()` on the animated element:
 * the enter comes from CSS `@starting-style` (no JS frame wait, so it can't stall on a busy main thread) and
 * the exit from `[data-ending-style]` (the base-ui attribute, so one CSS pattern styles every overlay). The
 * element stays mounted for `exitMs` after `open` goes false; reduced motion, SSR and jsdom unmount immediately.
 *
 * The exit state is derived DURING render (React's "adjust state when a prop changes" pattern), never in an
 * effect: an effect runs after the commit that already rendered `present: false`, so the element was unmounted
 * and re-created with `[data-ending-style]` — a new node with nothing to transition from, so every exit snapped.
 */
export function usePresence(open: boolean, exitMs = 160): { present: boolean; ending: boolean } {
  const [prevOpen, setPrevOpen] = useState(open);
  const [exiting, setExiting] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    // Closing: stay mounted for the exit. Reopening mid-exit: drop the exit; the transition reverses in place.
    setExiting(!open && motionAllowed());
  }
  useEffect(() => {
    if (!exiting) return undefined;
    const t = setTimeout(() => setExiting(false), exitMs);
    return () => clearTimeout(t);
  }, [exiting, exitMs]);
  return { present: open || exiting, ending: exiting && !open };
}
/** Spread onto the animated element: the base-ui exit attribute (enter is CSS @starting-style). */
export function presenceAttrs(p: { ending: boolean }): Record<string, string | undefined> {
  return { "data-ending-style": p.ending ? "" : undefined };
}

export type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";
export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
};

/** v2 button. One primary per view; `loading` keeps the width stable and announces busy state. */
export function Button({ variant = "secondary", size = "md", loading = false, className, children, disabled, type = "button", ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      className={cx("ffx-btn", variant !== "secondary" && `ffx-btn--${variant}`, size !== "md" && `ffx-btn--${size}`, className)}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      {...rest}
    >
      {children}
      {loading ? <span className="ffx-btn__spin" aria-hidden="true" /> : null}
    </button>
  );
}

export type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & { label: string; hint?: string; error?: string; suffix?: ReactNode; optional?: boolean };

/**
 * Labelled text input. Since the Oct 18 refinement this is the integrated-label TextField (label inside the
 * box, floats when focused or filled). `placeholder` becomes the example shown while focused and empty.
 */
export function Field({ placeholder, ...rest }: FieldProps) {
  return <TextField {...rest} example={typeof placeholder === "string" && placeholder.trim() ? placeholder : undefined} />;
}

export function Card({ title, actions, raised = false, children, className }: { title?: ReactNode; actions?: ReactNode; raised?: boolean; children: ReactNode; className?: string }) {
  return (
    <section className={cx("ffx-card", raised && "ffx-card--raised", className)}>
      {title || actions ? (
        <header className="ffx-card__head">
          {title ? <h3 className="ffx-card__title">{title}</h3> : <span />}
          {actions}
        </header>
      ) : null}
      <div className="ffx-card__body">{children}</div>
    </section>
  );
}

/** The one tone set for every badge, pill, banner and dot. `accent` is the brand lime (a highlight, never a status). */
export type PillTone = "neutral" | "success" | "info" | "warning" | "danger";
export type BadgeTone = PillTone | "accent";
export const BADGE_TONES: readonly BadgeTone[] = ["neutral", "info", "success", "warning", "danger", "accent"];
export type BadgeProps = {
  tone?: BadgeTone;
  /** sm: dense tables and tab counts; md (default): everywhere else. */
  size?: "sm" | "md";
  /** Leading status dot (default on). Off for plain labels ("Prospect", "Beta"). */
  dot?: boolean;
  /** Pulse the dot (in transit, live). */
  live?: boolean;
  /** Full text when the visible label is shortened. */
  title?: string;
  className?: string;
  children: ReactNode;
};
/**
 * Badge: a tinted pill in one of six tones and two sizes. Text always carries the meaning (color never alone);
 * the dot uses the tone's fill, the text its AA ink. Map app statuses to tones in one place: `statusBadge()`.
 */
export function Badge({ tone = "neutral", size = "md", dot = true, live = false, title, className, children }: BadgeProps) {
  return (
    <span title={title} className={cx("ffx-pill", tone !== "neutral" && `ffx-pill--${tone}`, size === "sm" && "ffx-pill--sm", !dot && "ffx-pill--nodot", live && "ffx-pill--live", className)}>
      {children}
    </span>
  );
}
/** Status pill with a coloured dot (= Badge). `live` pulses the dot (in-transit). Text always carries the meaning. */
export function StatusPill({ tone = "neutral", live = false, children, ...rest }: Omit<BadgeProps, "tone"> & { tone?: BadgeTone }) {
  return <Badge tone={tone} live={live} {...rest}>{children}</Badge>;
}

export function SegmentedControl<T extends string>({ label, options, value, onChange }: { label: string; options: ReadonlyArray<{ value: T; label: string }>; value: T; onChange: (v: T) => void }) {
  const index = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <div className="ffx-seg" role="radiogroup" aria-label={label}>
      <span className="ffx-seg__thumb" aria-hidden="true" style={{ width: `calc((100% - 0.375rem) / ${options.length})`, transform: `translateX(${index * 100}%)` }} />
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} tabIndex={o.value === value ? 0 : -1} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Build an SVG path for a sparkline in a 100x32 box. Exported for tests. */
export function sparkPath(values: readonly number[], w = 100, h = 32): { line: string; area: string } {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length < 2) return { line: "", area: "" };
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = max - min || 1;
  const pts = finite.map((v, i) => [(i / (finite.length - 1)) * w, h - 2 - ((v - min) / span) * (h - 6)] as const);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`).join(" ");
  return { line, area: `${line} L${w} ${h} L0 ${h} Z` };
}

export function Sparkline({ values, label }: { values: readonly number[]; label: string }) {
  const { line, area } = sparkPath(values);
  if (!line) return null;
  return (
    <svg className="ffx-spark" viewBox="0 0 100 32" preserveAspectRatio="none" role="img" aria-label={label}>
      <defs>
        <linearGradient id="ffx-spark-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#b2d235" stopOpacity="0.28" />
          <stop offset="1" stopColor="#b2d235" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path className="ffx-spark__area" d={area} />
      <path className="ffx-spark__line" d={line} pathLength={1} />
    </svg>
  );
}

/** KPI tile: micro-label, display number, delta vs previous period, sparkline bleeding to the edges. */
export function KpiTile({ label, value, delta, deltaContext = "vs last month", trend, goodWhen = "up", chart = "line", sub, tone: subTone }: {
  label: string;
  value: string;
  delta?: number;
  deltaContext?: string;
  trend?: readonly number[];
  goodWhen?: "up" | "down" | "neutral";
  chart?: "line" | "bars";
  /** One line under the figure ("3 overdue", "quoted $1,200"). Takes the trend's place when there is no trend. */
  sub?: ReactNode;
  /** Attention tone for `sub`. */
  tone?: "danger" | "warning";
}) {
  const hasDelta = typeof delta === "number" && Number.isFinite(delta);
  const good = hasDelta && (goodWhen === "up" ? delta >= 0 : delta <= 0);
  const tone = goodWhen === "neutral" ? "ffx-delta--flat" : good ? "ffx-delta--up" : "ffx-delta--down";
  return (
    <section className={cx("ffx-card ffx-kpi", !trend && "ffx-kpi--plain")} data-tone={subTone} aria-label={label}>
      <span className="ffx-micro">{label}</span>
      <div className="ffx-kpi__row">
        <span className="ffx-kpi__value">{value}</span>
        {hasDelta ? (
          <span className={cx("ffx-delta", tone)}>
            <span aria-hidden="true">{delta >= 0 ? "↑" : "↓"}</span>
            {Math.abs(delta).toFixed(1)}%<span className="ffx-delta__ctx">&nbsp;{deltaContext}</span>
          </span>
        ) : null}
      </div>
      {sub ? <span className="ffx-kpi__sub">{sub}</span> : null}
      {trend ? (chart === "bars" ? <SparkBars values={trend} label={`${label} trend`} /> : <Sparkline values={trend} label={`${label} trend`} />) : null}
    </section>
  );
}

export function Skeleton({ width = "100%", height = "0.875rem", radius }: { width?: string; height?: string; radius?: string }) {
  return <span className="ffx-skel" aria-hidden="true" style={{ width, height, borderRadius: radius }} />;
}

/** Empty state. Uses the chevron mark: put at most one per screen. */
export function EmptyState({ title, body, actions }: { title: string; body?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="ffx-empty">
      <LogoMark className="ffx-empty__mark" title="" />
      <h3 className="ffx-empty__title">{title}</h3>
      {body ? <p className="ffx-empty__body">{body}</p> : null}
      {actions ? <div className="ffx-empty__actions">{actions}</div> : null}
    </div>
  );
}

/** Initials avatar with a stable hue derived from the name. */
export function avatarHue(name: string): number {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase();
}
export function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" }) {
  return (
    <span className={cx("ffx-avatar", size === "sm" && "ffx-avatar--sm")} style={{ ["--h" as string]: avatarHue(name) }} role="img" aria-label={name}>
      {initials(name)}
    </span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="ffx-kbd">{children}</kbd>;
}

/** Bar heights (0-100, last bar highlighted) for a mini bar chart. Exported for tests. */
export function barHeights(values: readonly number[]): number[] {
  const finite = values.filter((v) => Number.isFinite(v));
  const max = Math.max(0, ...finite);
  if (finite.length === 0 || max <= 0) return finite.map(() => 0);
  return finite.map((v) => Math.max(4, Math.round((Math.max(0, v) / max) * 100)));
}
export function SparkBars({ values, label }: { values: readonly number[]; label: string }) {
  const hs = barHeights(values);
  if (hs.length === 0) return null;
  return (
    <span className="ffx-bars" role="img" aria-label={label}>
      {hs.map((h, i) => <span key={i} className={cx("ffx-bars__bar", i === hs.length - 1 && "ffx-bars__bar--now")} style={{ height: `${h}%`, animationDelay: `${i * 24}ms` }} />)}
    </span>
  );
}

export type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value" | "defaultValue"> & {
  label: string; hint?: string; options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
  value?: string; defaultValue?: string;
  /** Native-compatible: receives an event-like object whose `target.value` is the chosen option. */
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
};
/**
 * @deprecated Use `Select` (variant="field") from the control system. Kept as a compatible wrapper so old
 * call sites keep working: same props as a native select, but renders the FlexFreight listbox (never the OS menu).
 */
export function SelectField({ label, hint, options, id, className, value, defaultValue, onChange, name, disabled, required }: SelectFieldProps) {
  const controlled = value !== undefined;
  const [inner, setInner] = useState<string>(String(defaultValue ?? options[0]?.value ?? ""));
  const current = controlled ? String(value) : inner;
  return (
    <Select
      label={label} hint={hint} options={options} value={current} id={id} name={name} disabled={disabled} required={required} className={className}
      onChange={(v) => {
        if (!controlled) setInner(v);
        const target = { value: v, name: name ?? "" } as HTMLSelectElement;
        onChange?.({ target, currentTarget: target } as unknown as ChangeEvent<HTMLSelectElement>);
      }}
    />
  );
}

/** Next tab index for arrow/Home/End keys (WAI-ARIA tabs, automatic activation). Exported for tests. */
export function nextTabIndex(key: string, current: number, count: number): number {
  if (count <= 0) return current;
  if (key === "ArrowRight") return (current + 1) % count;
  if (key === "ArrowLeft") return (current - 1 + count) % count;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return current;
}
export function Tabs<T extends string>({ label, tabs, value, onChange, idBase }: { label: string; tabs: ReadonlyArray<{ value: T; label: string; count?: number }>; value: T; onChange: (v: T) => void;
  /** Links each tab to the `TabPanel` with the same idBase (aria-controls / aria-labelledby). */
  idBase?: string }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const index = Math.max(0, tabs.findIndex((t) => t.value === value));
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    // From the focused tab (not `value`, which may still be catching up when keys repeat quickly).
    const from = refs.current.indexOf(e.target as HTMLButtonElement);
    const cur = from >= 0 ? from : index;
    const n = nextTabIndex(e.key, cur, tabs.length);
    if (n === cur) return;
    e.preventDefault();
    const t = tabs[n];
    if (t) { onChange(t.value); refs.current[n]?.focus(); }
  };
  return (
    <div className="ffx-tabs" role="tablist" aria-label={label} onKeyDown={onKey}>
      {tabs.map((t, i) => (
        <button key={t.value} ref={(el) => { refs.current[i] = el; }} type="button" role="tab" aria-selected={i === index} tabIndex={i === index ? 0 : -1} className="ffx-tab" onClick={() => onChange(t.value)}
          id={idBase ? `${idBase}-tab-${t.value}` : undefined} aria-controls={idBase && i === index ? `${idBase}-panel` : undefined}>
          {t.label}
          {typeof t.count === "number" ? <span className="ffx-tab__count ffx-num">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/* ---------- P.2.3: collision-aware floating placement (Tooltip, Combobox, DatePicker) ---------- */

export type Side = "top" | "bottom";
export type Align = "start" | "center" | "end";
export type Rect = { left: number; top: number; width: number; height: number };
/**
 * Place a floating box against an anchor inside the viewport: prefer `side`, flip to the other side when it
 * doesn't fit and the other side has more room, then shift along the x axis so it stays `pad` from both edges.
 * Coordinates are viewport (position: fixed), so ancestor overflow/scroll containers never clip it.
 */
export function placeFloating(anchor: Rect, box: { width: number; height: number }, viewport: { width: number; height: number },
  { side = "bottom", align = "start", gap = 6, pad = 8 }: { side?: Side; align?: Align; gap?: number; pad?: number } = {}): { left: number; top: number; side: Side; maxHeight: number } {
  const below = viewport.height - (anchor.top + anchor.height) - gap - pad;
  const above = anchor.top - gap - pad;
  let s: Side = side;
  if (s === "bottom" && box.height > below && above > below) s = "top";
  else if (s === "top" && box.height > above && below > above) s = "bottom";
  const room = s === "bottom" ? below : above;
  const h = Math.min(box.height, Math.max(room, 0));
  const top = s === "bottom" ? anchor.top + anchor.height + gap : anchor.top - gap - h;
  let left = align === "start" ? anchor.left : align === "end" ? anchor.left + anchor.width - box.width : anchor.left + (anchor.width - box.width) / 2;
  left = Math.min(left, viewport.width - pad - box.width);
  left = Math.max(left, pad);
  return { left: Math.round(left), top: Math.round(top), side: s, maxHeight: Math.floor(Math.max(room, 0)) };
}

/** True when an element with this computed style becomes the containing block for `position: fixed` descendants. */
export function trapsFixed(cs: Pick<CSSStyleDeclaration, "transform" | "filter" | "perspective" | "contain" | "willChange" | "backdropFilter">): boolean {
  const set = (v: string | undefined) => !!v && v !== "none" && v !== "";
  return set(cs.transform) || set(cs.filter) || set(cs.perspective) || set(cs.backdropFilter)
    || /paint|layout|strict|content/.test(cs.contain ?? "") || /transform|filter|perspective/.test(cs.willChange ?? "");
}
/** Offset of the nearest ancestor that traps `position: fixed` (0,0 when none), so viewport coords can be corrected. */
function fixedOrigin(el: HTMLElement): { x: number; y: number } {
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    if (trapsFixed(getComputedStyle(n))) { const r = n.getBoundingClientRect(); return { x: r.left + n.clientLeft, y: r.top + n.clientTop }; }
  }
  return { x: 0, y: 0 };
}

type ClipStyle = Pick<CSSStyleDeclaration, "transform" | "filter" | "perspective" | "contain" | "willChange" | "backdropFilter" | "overflowX" | "overflowY">;
/**
 * Ancestor styles from nearest to farthest. True when some ancestor traps `position: fixed` AND that ancestor
 * (or one between it and the element) clips overflow: then a fixed float is cut off and must portal to <body>.
 */
export function needsPortal(chain: readonly ClipStyle[]): boolean {
  let clipped = false;
  for (const cs of chain) {
    if (/hidden|clip|scroll|auto/.test(`${cs.overflowX ?? ""} ${cs.overflowY ?? ""}`) || /paint|strict|content/.test(cs.contain ?? "")) clipped = true;
    if (trapsFixed(cs)) return clipped;
  }
  return false;
}
/** Measures `anchor`'s ancestors once per open; true when the float must render through a body portal. */
export function usePortalNeed(open: boolean, anchor: RefObject<HTMLElement | null>): boolean {
  const [need, setNeed] = useState(false);
  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") { setNeed(false); return; }
    const chain: ClipStyle[] = [];
    for (let n = anchor.current?.parentElement ?? null; n && n !== document.body; n = n.parentElement) chain.push(getComputedStyle(n));
    setNeed(needsPortal(chain));
  }, [open, anchor]);
  return need;
}

/** Keeps `float` placed against `anchor` while `open`, re-placing on scroll (any container) and resize. */
export function useFloating(open: boolean, anchor: RefObject<HTMLElement | null>, float: RefObject<HTMLElement | null>,
  opts: { side?: Side; align?: Align; gap?: number; matchWidth?: boolean } = {}): { style: CSSProperties; side: Side } {
  const [pos, setPos] = useState<{ style: CSSProperties; side: Side }>({ style: {}, side: opts.side ?? "bottom" });
  const { side, align, gap, matchWidth } = opts;
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const a = anchor.current; const f = float.current;
      if (!a || !f || typeof window === "undefined") return;
      const r = a.getBoundingClientRect();
      const width = matchWidth ? r.width : f.offsetWidth;
      const p = placeFloating({ left: r.left, top: r.top, width: r.width, height: r.height }, { width, height: f.scrollHeight },
        { width: document.documentElement.clientWidth, height: window.innerHeight }, { side, align, gap });
      const o = fixedOrigin(f);
      setPos({ side: p.side, style: { position: "fixed", left: p.left - o.x, top: p.top - o.y, right: "auto", bottom: "auto", ["--ffx-room" as string]: `${p.maxHeight}px`, ...(matchWidth ? { width } : {}) } as CSSProperties });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    // Re-place when the float's own size changes (async results arrive, a month with 6 weeks): a list flipped
    // above its field must grow upward, not down over the field it belongs to.
    const ro = typeof ResizeObserver === "function" && float.current ? new ResizeObserver(update) : null;
    if (ro && float.current) ro.observe(float.current);
    return () => { window.removeEventListener("scroll", update, true); window.removeEventListener("resize", update); ro?.disconnect(); };
  }, [open, anchor, float, side, align, gap, matchWidth]);
  return pos;
}

/* Tooltip timing is shared: hover waits for intent (--ff-delay-tip, read from CSS so tokens own it); once one
   tooltip has shown, the next one within WARM_MS opens instantly (moving along a toolbar shouldn't re-wait). */
let lastTipClose = 0;
const WARM_MS = 500;
function tipDelay(el: HTMLElement | null): number {
  if (!el || typeof getComputedStyle !== "function") return 0;
  const v = getComputedStyle(el).getPropertyValue("--ff-delay-tip").trim();
  const n = parseFloat(v);
  return Number.isFinite(n) ? (v.endsWith("ms") ? n : n * 1000) : 0;
}

/** Tooltip on hover (after a short intent delay) and keyboard focus (immediately); text is also the trigger's accessible description. */
export function Tooltip({ content, children, align = "center", when }: { content: string; children: ReactNode; align?: "center" | "end";
  /** Show only when this returns true at open time (e.g. the text is actually truncated). */
  when?: () => boolean }) {
  const id = useId();
  const [shown, setShown] = useState(false);
  // Warm (another tooltip just closed): skip the fade too, not only the delay, so scanning a row of icons feels instant.
  const [warm, setWarm] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { style, side } = useFloating(shown, wrap, bubble, { side: "top", align, gap: 8 });
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = (instant: boolean) => {
    clearTimeout(timer.current);
    if (when && !when()) return;
    const isWarm = Date.now() - lastTipClose < WARM_MS;
    setWarm(isWarm);
    const wait = instant || isWarm ? 0 : tipDelay(wrap.current);
    if (wait <= 0) setShown(true);
    else timer.current = setTimeout(() => setShown(true), wait);
  };
  const hide = () => {
    clearTimeout(timer.current);
    setShown((was) => { if (was) lastTipClose = Date.now(); return false; });
  };
  return (
    <span ref={wrap} className={cx("ffx-tip", align === "end" && "ffx-tip--end")} aria-describedby={id}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") show(false); }} onPointerLeave={hide}
      onFocus={(e) => show(e.target instanceof HTMLElement && e.target.matches(":focus-visible"))} onBlur={hide}
      onKeyDown={(e) => { if (e.key === "Escape" && shown) { e.stopPropagation(); hide(); } }}>
      {children}
      <span ref={bubble} role="tooltip" id={id} className={cx("ffx-tip__bubble", style.position && "is-floating")} data-open={shown ? "" : undefined} data-instant={warm || undefined} data-side={side} style={style}>{content}</span>
    </span>
  );
}

// The table primitive lives in table.tsx (sorting, selection, pagination, states); re-exported for old imports.
export { DataTable, type Column } from "./table.js";

/** Where Tab should move inside a trap: wraps first<->last. Exported for tests. */
export function trapTarget(shift: boolean, activeIndex: number, count: number): number | null {
  if (count === 0) return null;
  if (shift && activeIndex <= 0) return count - 1;
  if (!shift && activeIndex === count - 1) return 0;
  return null;
}
/**
 * Modal dialog or side drawer, built on base-ui Dialog (the same engine as `useConfirm` and the filter sheet):
 * focus trap, Escape, outside press, scroll lock, focus return to the opener, nested-dialog stacking (render a
 * `useConfirm` dialog inside `children` and it stacks on top). Presence is base-ui's attribute contract:
 * 180ms in / 120ms out (drawer: slide 280 / 200), and reopening mid-exit reverses. Put decisions in `footer`.
 */
export type DialogTotal = { label: string; value: string; strong?: boolean };
export type DialogProps = {
  open: boolean; onClose: () => void; title: string; description?: string; children?: ReactNode; footer?: ReactNode; totals?: readonly DialogTotal[]; variant?: "modal" | "drawer";
  /** Element to focus on open (default: the first focusable element, the Close button). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Width: sm 24rem, md 28rem (default), lg 40rem; drawers md 30rem, lg 40rem. */
  size?: "sm" | "md" | "lg";
};
export function Dialog({ open, onClose, title, description, children, footer, totals, variant = "modal", initialFocus, size = "md" }: DialogProps) {
  const drawer = variant === "drawer";
  return (
    <BaseDialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={cx("ffx-backdrop", drawer && "ffx-backdrop--drawer")} />
        <BaseDialog.Viewport className={cx("ffx-overlay", drawer && "ffx-overlay--drawer")}>
          <BaseDialog.Popup className={cx("ffx-dialog", drawer && "ffx-dialog--drawer", size !== "md" && `ffx-dialog--${size}`)} initialFocus={initialFocus}>
            <header className="ffx-dialog__head">
              <BaseDialog.Title className="ffx-dialog__title">{title}</BaseDialog.Title>
              <BaseDialog.Close className="ffx-dialog__close" aria-label="Close"><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg></BaseDialog.Close>
            </header>
            {description ? <BaseDialog.Description className="ffx-dialog__desc">{description}</BaseDialog.Description> : null}
            {children ? <div className="ffx-dialog__body">{children}</div> : null}
            {totals?.length || footer ? (
              <div className="ffx-dialog__dock">
                {totals?.length ? (
                  <dl className="ffx-dialog__totals">
                    {totals.map((t) => (
                      <div key={t.label} className={cx("ffx-dialog__total", t.strong && "is-strong")}><dt>{t.label}</dt><dd>{t.value}</dd></div>
                    ))}
                  </dl>
                ) : null}
                {footer ? <footer className="ffx-dialog__foot">{footer}</footer> : null}
              </div>
            ) : null}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

export type ToastItem = { id: number; tone: "success" | "info" | "danger"; title: string; body?: string };
/**
 * @deprecated The portal has one notification surface: `notify` from apps/web/src/shell/Toaster (Sonner).
 * Kept (styled like it) only so old call sites and tests compile. Newest last, auto-dismiss after `ms`, max 3.
 */
export function useToasts(ms = 5000) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const push = useCallback((t: Omit<ToastItem, "id">) => {
    const id = ++seq.current;
    setItems((xs) => [...xs, { ...t, id }].slice(-3));
    if (ms > 0) setTimeout(() => dismiss(id), ms);
    return id;
  }, [dismiss, ms]);
  return { items, push, dismiss };
}
/** @deprecated Render `<Toaster />` once and call `notify.*` instead. */
export function ToastViewport({ items, onDismiss }: { items: readonly ToastItem[]; onDismiss: (id: number) => void }) {
  return (
    <ol className="ffx-toasts" aria-live="polite" aria-label="Notifications">
      {items.map((t) => (
        <li key={t.id} className={cx("ffx-toast", `ffx-toast--${t.tone}`)} role={t.tone === "danger" ? "alert" : "status"}>
          <span className="ffx-toast__dot" aria-hidden="true" />
          <div className="ffx-toast__text"><strong>{t.title}</strong>{t.body ? <span>{t.body}</span> : null}</div>
          <button type="button" className="ffx-dialog__close" aria-label="Dismiss" onClick={() => onDismiss(t.id)}><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg></button>
        </li>
      ))}
    </ol>
  );
}

/* ---------- P.2.2: Combobox (async autocomplete) ---------- */

export type ComboOption = { value: string; label: string; detail?: string };

/** Next highlighted index for listbox arrow keys; -1 means nothing highlighted. Wraps at both ends. */
export function nextOptionIndex(key: string, current: number, count: number): number {
  if (count <= 0) return -1;
  if (key === "ArrowDown") return current < 0 || current >= count - 1 ? 0 : current + 1;
  if (key === "ArrowUp") return current <= 0 ? count - 1 : current - 1;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return current;
}

/** Options whose label or detail contains every typed word (case- and accent-insensitive), label-prefix matches first. */
export function filterOptions(options: readonly ComboOption[], query: string): ComboOption[] {
  const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const words = norm(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...options];
  const hits = options.filter((o) => { const hay = norm(`${o.label} ${o.detail ?? ""} ${o.value}`); return words.every((w) => hay.includes(w)); });
  const first = words[0]!;
  return hits.sort((a, b) => Number(!norm(a.label).startsWith(first)) - Number(!norm(b.label).startsWith(first)));
}

export type ComboboxProps = {
  label: string; hint?: ReactNode; placeholder?: string; id?: string; minChars?: number; debounceMs?: number; emptyText?: string;
  /** Async source: called (debounced) with the typed text; stale responses are dropped. */
  load?: (query: string) => Promise<readonly ComboOption[]>;
  /** Static source, filtered as you type (opens on focus; `minChars` defaults to 0). */
  options?: readonly ComboOption[];
  /** Fires on every pick (address autocomplete). */
  onSelect?: (option: ComboOption) => void;
  /** Selection mode: the chosen value. The box shows its label when not being edited; blur restores it. */
  value?: string | null;
  onChange?: (value: string | null, option: ComboOption | null) => void;
  /** Selection mode: label of `value` when it isn't in `options` (async sources). */
  valueLabel?: string;
  /** Offer a clear button while a value is chosen (calls onChange(null, null)). */
  clearable?: boolean;
  error?: ReactNode; optional?: boolean; disabled?: boolean; name?: string;
  size?: "sm" | "md";
  /** Leading search glyph (default on for async search, off for a picker over static options). */
  icon?: boolean;
  className?: string;
};

/**
 * Searchable combobox: async autocomplete (address / ZIP, `load`) or a searchable picker over static `options`
 * (`value` + `onChange`). WAI-ARIA 1.2 combobox: the input keeps focus, options are referenced by
 * aria-activedescendant; arrows move, Enter picks, Escape closes (then clears or restores), typing filters.
 */
export function Combobox({ label, hint, placeholder, load, options: staticOptions, onSelect, onChange, value, valueLabel, clearable, error, optional, disabled, name, size = "md", icon, className, id, minChars: minCharsProp, debounceMs = 160, emptyText = "No matches. Check the spelling or try a ZIP code." }: ComboboxProps) {
  const auto = useId();
  const inputId = id ?? `c${auto.replace(/:/g, "")}`;
  const listId = `${inputId}-list`;
  const picker = value !== undefined;
  const minChars = minCharsProp ?? (staticOptions ? 0 : 2);
  const selected = picker && value ? staticOptions?.find((o) => o.value === value) ?? (valueLabel ? { value, label: valueLabel } : null) : null;
  const [text, setText] = useState(selected?.label ?? "");
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [remote, setRemote] = useState<readonly ComboOption[]>([]);
  const [active, setActive] = useState(-1);
  const seq = useRef(0);
  const controlRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Picker: while not editing, the box mirrors the chosen value's label (the parent may change it).
  const shownText = picker && !editing ? (selected?.label ?? "") : text;
  const query = editing || !picker ? text.trim() : "";
  const local = staticOptions ? filterOptions(staticOptions, query) : null;
  const options = local ?? remote;
  useEffect(() => {
    if (!load || staticOptions) return;
    const q = text.trim();
    if (q.length < minChars) { setRemote([]); setBusy(false); return; }
    const mine = ++seq.current;
    setBusy(true);
    const t = setTimeout(() => {
      load(q).then((res) => { if (mine === seq.current) { setRemote(res); setActive(res.length ? 0 : -1); setBusy(false); } },
        () => { if (mine === seq.current) { setRemote([]); setBusy(false); } });
    }, debounceMs);
    return () => clearTimeout(t);
  }, [text, load, staticOptions, minChars, debounceMs]);
  const choose = (o: ComboOption) => {
    setText(o.label); setEditing(false); setOpen(false); seq.current++; setRemote([]);
    onSelect?.(o); onChange?.(o.value, o);
  };
  const expanded = open && !disabled && (staticOptions ? true : text.trim().length >= minChars);
  const { style: listStyle, side: listSide } = useFloating(expanded, controlRef, listRef, { side: "bottom", align: "start", gap: 6, matchWidth: true });
  const portal = usePortalNeed(expanded, controlRef);
  const openList = () => {
    setOpen(true);
    if (local) { const i = selected ? local.findIndex((o) => o.value === selected.value) : -1; setActive(i >= 0 ? i : local.length ? 0 : -1); }
  };
  const restore = () => { setEditing(false); setText(selected?.label ?? ""); };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); if (!open) openList(); setActive((a) => nextOptionIndex(e.key, a, options.length)); }
    else if (e.key === "Enter" && expanded && active >= 0 && options[active]) { e.preventDefault(); choose(options[active]); }
    else if (e.key === "Escape") {
      if (expanded) { e.preventDefault(); setOpen(false); if (picker) restore(); }
      else if (picker) restore();
      else setText("");
    }
  };
  const desc = [error ? `${inputId}-err` : hint ? `${inputId}-hint` : null].filter(Boolean).join(" ") || undefined;
  const showIcon = icon ?? !staticOptions;
  // Same field as TextField (integrated floating label, --ff-field-h); the list is the shared menu surface.
  const list = (
    <ul ref={listRef} id={listId} role="listbox" aria-label={label} className={cx("ffc-menu ffx-combo__list", listStyle.position && "is-floating")} data-side={listSide} style={listStyle} hidden={!expanded}>
      {busy && options.length === 0 ? <li className="ffx-combo__status" role="presentation">Searching…</li> : null}
      {!busy && options.length === 0 ? <li className="ffx-combo__status" role="presentation">{emptyText}</li> : null}
      {options.map((o, i) => (
        <li key={o.value} id={`${listId}-${i}`} role="option" aria-selected={i === active} data-highlighted={i === active ? "" : undefined} data-selected={selected?.value === o.value ? "" : undefined} className="ffc-opt ffx-combo__opt"
          onMouseDown={(e) => { e.preventDefault(); choose(o); }} onMouseMove={() => setActive(i)}>
          <span className="ffx-combo__label">{o.label}</span>
          {o.detail ? <span className="ffx-combo__detail">{o.detail}</span> : null}
        </li>
      ))}
    </ul>
  );
  return (
    <div className={cx("ffc-field ffx-combo", size === "sm" && "ffc-field--sm", error ? "is-invalid" : null, disabled ? "is-disabled" : null, picker && "ffx-combo--picker", className)}>
      <div className="ffc-box ffx-combo__control" ref={controlRef}>
        {showIcon ? (
          <span className="ffc-affix ffc-affix--pre ffc-affix--icon" aria-hidden="true">
            <svg className="ffc-ico" viewBox="0 0 16 16"><circle cx="7" cy="7" r="4.25" /><path d="m10.25 10.25 3 3" /></svg>
          </span>
        ) : null}
        <input
          ref={inputRef}
          id={inputId} className="ffc-input ffx-combo__input" role="combobox" autoComplete="off" spellCheck={false} disabled={disabled}
          aria-expanded={expanded} aria-controls={listId} aria-autocomplete="list" aria-busy={busy || undefined}
          aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
          aria-describedby={desc} aria-invalid={error ? true : undefined}
          placeholder=" " value={shownText}
          onChange={(e) => { setText(e.target.value); setEditing(true); setOpen(true); if (local) setActive(0); }}
          onKeyDown={onKey}
          onBlur={() => { setOpen(false); if (picker) restore(); }}
          onFocus={() => { if (picker) { setText(selected?.label ?? ""); } openList(); }}
          onClick={() => { if (!open) openList(); }}
        />
        <label htmlFor={inputId} className="ffc-label">{label}{optional ? <span className="ffc-optional"> Optional</span> : null}</label>
        {placeholder ? <span className="ffc-example" aria-hidden="true">{placeholder}</span> : null}
        {busy ? <span className="ffx-combo__busy" aria-hidden="true" /> : null}
        {picker && clearable && selected && !disabled ? (
          <button type="button" className="ffc-search__clear ffx-combo__clear" aria-label={`Clear ${label}`} onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setText(""); setEditing(false); onChange?.(null, null); inputRef.current?.focus(); }}>
            <svg className="ffc-ico" viewBox="0 0 16 16" aria-hidden="true"><path d="m4.5 4.5 7 7m0-7-7 7" /></svg>
          </button>
        ) : null}
        {picker && !busy ? <span className="ffc-trigger__icon ffx-combo__chev" aria-hidden="true"><svg className="ffc-chev" viewBox="0 0 16 16"><path d="M4.5 6.25 8 9.75l3.5-3.5" /></svg></span> : null}
        {portal && expanded ? createPortal(list, document.body) : list}
      </div>
      {name ? <input type="hidden" name={name} value={value ?? ""} /> : null}
      {picker || error ? <p className="ffc-msg ffc-msg--error" id={`${inputId}-err`} aria-live="polite">{error || null}</p> : null}
      {hint && !error ? <p id={`${inputId}-hint`} className="ffc-msg">{hint}</p> : null}
      <span className="ffx-sr" aria-live="polite">{expanded && !busy ? `${options.length} ${options.length === 1 ? "result" : "results"}` : ""}</span>
    </div>
  );
}

/* ---------- P.2.2: DatePicker (pickup date, business days, cutoff) ---------- */

const pad2 = (n: number) => String(n).padStart(2, "0");
/** Local-date ISO (YYYY-MM-DD), never UTC-shifted. */
export function isoDate(d: Date): string { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
export function parseIso(s: string): Date { const [y, m, d] = s.split("-").map(Number); return new Date(y!, (m ?? 1) - 1, d ?? 1); }
export function addDays(d: Date, n: number): Date { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
export function isBusinessDay(d: Date, holidays: ReadonlySet<string> = new Set()): boolean {
  const w = d.getDay();
  return w !== 0 && w !== 6 && !holidays.has(isoDate(d));
}
/** Earliest pickup date: today if before the cutoff hour and a business day, else the next business day. */
export function earliestPickup(now: Date, cutoffHour: number, holidays: ReadonlySet<string> = new Set()): Date {
  let d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (!(isBusinessDay(d, holidays) && now.getHours() < cutoffHour)) d = addDays(d, 1);
  while (!isBusinessDay(d, holidays)) d = addDays(d, 1);
  return d;
}
/** The next `count` business days starting at `from` (inclusive). */
export function businessDays(from: Date, count: number, holidays: ReadonlySet<string> = new Set()): Date[] {
  const out: Date[] = [];
  for (let d = from; out.length < count; d = addDays(d, 1)) if (isBusinessDay(d, holidays)) out.push(d);
  return out;
}
/** 6x7 grid of dates for the month view, weeks starting Sunday. */
export function monthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const start = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}
const fmtHour = (h: number) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;
const DOW = ["S", "M", "T", "W", "T", "F", "S"];

/**
 * Week rows (0-5) the phone sheet shows for a month grid: drops weeks that end before the current week
 * (nothing there can be picked) and trailing weeks that sit wholly in the next month.
 */
export function sheetWeeks(grid: readonly Date[], month: number, now: Date): number[] {
  const weekStart = addDays(new Date(now.getFullYear(), now.getMonth(), now.getDate()), -now.getDay());
  const rows: number[] = [];
  for (let w = 0; w < 6; w++) {
    const days = grid.slice(w * 7, w * 7 + 7);
    if (days[6]! < weekStart) continue;
    if (days.every((d) => d.getMonth() !== month)) continue;
    rows.push(w);
  }
  return rows;
}

const PHONE_QUERY = "(max-width: 40em)";
/** True at phone widths; popovers become bottom sheets there. SSR/jsdom without matchMedia -> false. */
export function usePhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

/** P.2.7: named holidays in month (y, m) from the earliest pickup on, sorted, so phones (no hover) can read why a day is closed. */
export function holidayNotes(names: ReadonlyMap<string, string> | null, y: number, m: number, earliest: Date): [string, string][] {
  if (!names) return [];
  const prefix = `${y}-${pad2(m + 1)}-`;
  return [...names].filter(([iso]) => iso.startsWith(prefix) && iso >= isoDate(earliest)).sort(([a], [b]) => (a < b ? -1 : 1));
}
/** P.2.6: how a calendar day reads. Past days recede; weekends/holidays from the earliest pickup on are "closed" (quiet, never an error). */
export function dayState(d: Date, earliest: Date, holidays: ReadonlySet<string> = new Set()): "open" | "past" | "closed" {
  if (d < earliest) return "past";
  return isBusinessDay(d, holidays) ? "open" : "closed";
}

function CalendarGlyph() {
  return <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="3.5" width="11" height="10" rx="2" /><path d="M2.5 6.5h11M5.5 2v3M10.5 2v3" /></svg>;
}

export type DatePickerProps = {
  label: string;
  /** ISO date (YYYY-MM-DD) or null. */
  value: string | null;
  onChange: (iso: string) => void;
  now?: Date;
  /** Same-day cutoff hour (local). `null`: today stays pickable all day (the server applies each carrier's own cutoff). */
  cutoffHour?: number | null;
  /** ISO dates the carrier is closed; pass a Map (ISO -> name, e.g. "Thanksgiving") to show holiday names inline. */
  holidays?: ReadonlySet<string> | ReadonlyMap<string, string>;
  id?: string;
  /**
   * "chips" (default): quick pickup days + "Other date". "field": one form-height box with a floating label, like
   * `Select`. "toolbar": compact "Label  Value" box at control height, for filter/toolbar rows.
   */
  variant?: "chips" | "field" | "toolbar";
  /** "pickup" (default): business days from the earliest pickup on; weekends, holidays and past days are refused. "any": every day. */
  rule?: "pickup" | "any";
  /** Field/toolbar: replaces the built-in hint (pass null for none). */
  hint?: ReactNode;
  error?: ReactNode;
  /** Field: shows the date without a picker (set elsewhere, e.g. on the quote). */
  readOnly?: boolean;
  /** Submits the ISO value through a hidden input. */
  name?: string;
  className?: string;
  /** Field: marks the label "Optional" and offers "Clear date" in the calendar (onChange("")). */
  optional?: boolean;
};

/**
 * Branded date picker (never the OS picker). Business-day aware: with `rule="pickup"` weekends, carrier holidays
 * and days before the earliest pickup can't be chosen, by click or keyboard. Popover on desktop (keyboard grid,
 * Escape and outside press close, focus returns to the trigger); bottom sheet on phones.
 */
export function DatePicker({ label, value, onChange, now = new Date(), cutoffHour = 15, holidays, id, variant = "chips", rule = "pickup", hint: hintProp, error, readOnly, name, className, optional }: DatePickerProps) {
  const auto = useId();
  const baseId = id ?? auto;
  const holNames: ReadonlyMap<string, string> | null = holidays instanceof Map ? holidays : null;
  const hol: ReadonlySet<string> = holNames ? new Set(holNames.keys()) : (holidays as ReadonlySet<string> | undefined) ?? new Set<string>();
  const any = rule === "any";
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const earliest = any ? today0 : earliestPickup(now, cutoffHour ?? 24, hol);
  const selected = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseIso(value) : null;
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState<Date>(selected ?? earliest);
  const [view, setView] = useState({ y: (selected ?? earliest).getFullYear(), m: (selected ?? earliest).getMonth() });
  const gridRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hintRef = useRef<HTMLSpanElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const chips = variant === "chips";
  const sheet = usePhone();
  const pp = usePresence(open, sheet ? 200 : 120);
  const anchor = chips ? hintRef : boxRef;
  const { style: popStyle, side: popSide } = useFloating(open && !sheet, anchor, popRef, { side: "bottom", align: chips ? "end" : "start", gap: 6 });
  const portal = usePortalNeed(open && !sheet, anchor);
  const quick = any ? [] : businessDays(earliest, 3, hol);
  const todayIso = isoDate(now);
  const pastCutoff = !any && cutoffHour !== null && isoDate(earliest) !== todayIso && isBusinessDay(now, hol);
  const fmtLong = (d: Date) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const fmtValue = (d: Date) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) });
  const selectable = (d: Date) => (any ? true : isBusinessDay(d, hol) && d >= earliest);
  const state = (d: Date) => (any ? "open" : dayState(d, earliest, hol));

  useEffect(() => {
    if (!open) return;
    const el = gridRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${isoDate(focus)}"]`);
    el?.focus();
  }, [open, focus]);
  // Outside press closes (the trigger toggles on its own; focus stays where the press landed).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t || popRef.current?.contains(t) || triggerRef.current?.contains(t) || boxRef.current?.contains(t)) return;
      if ((t as Element).closest?.(".ffx-sheet-scrim")) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  const close = () => { setOpen(false); triggerRef.current?.focus(); };
  const move = (n: number) => { const d = addDays(focus, n); setFocus(d); setView({ y: d.getFullYear(), m: d.getMonth() }); };
  const pick = (d: Date) => { if (!selectable(d)) return; onChange(isoDate(d)); close(); };
  const onGridKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (e.key in step) { e.preventDefault(); move(step[e.key]!); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(focus); }
  };
  const onPopKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
  };
  const toggle = () => {
    const start = selected ?? earliest;
    setFocus(start);
    setView({ y: start.getFullYear(), m: start.getMonth() });
    setOpen((o) => !o);
  };
  const shiftMonth = (n: number) => { const d = new Date(view.y, view.m + n, 1); setView({ y: d.getFullYear(), m: d.getMonth() }); };
  const monthName = new Date(view.y, view.m, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const builtInHint = any
    ? null
    : pastCutoff
      ? `Past today's ${fmtHour(cutoffHour ?? 15)} cutoff. Earliest pickup is ${fmtLong(earliest)}.`
      : cutoffHour === null
        ? "Weekends and holidays are closed."
        : `Book by ${fmtHour(cutoffHour)} for same-day pickup. Weekends and holidays are closed.`;
  const hint = hintProp !== undefined ? hintProp : builtInHint;

  const quickChips = (inPop = false) => quick.map((d) => {
    const iso = isoDate(d);
    const nm = iso === todayIso ? "Today" : iso === isoDate(addDays(now, 1)) ? "Tomorrow" : d.toLocaleDateString("en-US", { weekday: "long" });
    return (
      <button key={iso} type="button" className="ffx-date__chip" aria-pressed={value === iso}
        onClick={() => { onChange(iso); if (inPop) close(); }}>
        <span className="ffx-date__chip-name">{nm}</span>
        <span className="ffx-date__chip-date">{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
      </button>
    );
  });
  const grid = monthGrid(view.y, view.m);
  const weeks = sheet && !any ? sheetWeeks(grid, view.m, now) : [0, 1, 2, 3, 4, 5];
  const atNowMonth = view.y === now.getFullYear() && view.m === now.getMonth();
  const closedNotes = any ? [] : holidayNotes(holNames, view.y, view.m, earliest);
  const pop = pp.present ? (
        <div ref={popRef} className={cx("ffx-date__pop", sheet ? "ffx-date__pop--sheet" : "ffc-menu is-floating")} data-side={sheet ? undefined : popSide} style={sheet ? undefined : popStyle}
          {...presenceAttrs(pp)} inert={!open || undefined} onKeyDown={onPopKey}
          role="dialog" aria-modal={sheet} aria-label={`Choose ${label.toLowerCase()}`}>
          {sheet ? (
            <div className="ffx-sheet__top">
              <span className="ffx-sheet__grabber" aria-hidden="true" />
              <div className="ffx-sheet__bar">
                <span className="ffx-sheet__title">{label}</span>
                <button type="button" className="ffx-sheet__done" onClick={close}>Done</button>
              </div>
              {quick.length ? <div className="ffx-date__quick ffx-date__quick--sheet" role="group" aria-label="Next pickup days">{quickChips(true)}</div> : null}
            </div>
          ) : !chips && quick.length ? (
            <div className="ffx-date__quick ffx-date__quick--pop" role="group" aria-label="Next pickup days">{quickChips(true)}</div>
          ) : null}
          <div className="ffx-date__head">
            <button type="button" className="ffx-date__nav" aria-label="Previous month" disabled={sheet && !any && atNowMonth} onClick={() => shiftMonth(-1)}><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3 5 8l5 5" /></svg></button>
            <span className="ffx-date__month" aria-live="polite">{monthName}</span>
            <button type="button" className="ffx-date__nav" aria-label="Next month" onClick={() => shiftMonth(1)}><svg viewBox="0 0 16 16" aria-hidden="true"><path d="m6 3 5 5-5 5" /></svg></button>
          </div>
          <div className="ffx-date__grid" role="grid" aria-label={monthName} ref={gridRef} onKeyDown={onGridKey}>
            <div role="row" className="ffx-date__row">{DOW.map((d, i) => <span key={i} role="columnheader" className="ffx-date__dow" aria-hidden="true">{d}</span>)}</div>
            {weeks.map((w) => (
              <div role="row" className="ffx-date__row" key={w}>
                {grid.slice(w * 7, w * 7 + 7).map((d) => {
                  const iso = isoDate(d);
                  const ok = selectable(d);
                  const closed = state(d) === "closed";
                  return (
                    <span role="gridcell" key={iso} aria-selected={value === iso}>
                      <button type="button" data-iso={iso} tabIndex={iso === isoDate(focus) ? 0 : -1} aria-disabled={!ok || undefined}
                        aria-label={`${d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}${ok ? "" : closed ? `, closed${holNames?.get(iso) ? ` for ${holNames.get(iso)}` : ""}` : ", unavailable"}`} title={closed ? (hol.has(iso) ? `Closed: ${holNames?.get(iso) ?? "holiday"}` : "Closed: weekend") : undefined}
                        className={cx("ffx-date__day", d.getMonth() !== view.m && "is-out", iso === todayIso && "is-today", value === iso && "is-selected", !ok && "is-off", !ok && d < earliest && "is-past", closed && "is-closed")}
                        onClick={() => { setFocus(d); pick(d); }}>
                        {d.getDate()}
                      </button>
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
          {closedNotes.length ? (
            <ul className="ffx-date__closed" aria-label="Carrier holidays this month">
              {closedNotes.map(([iso, nm]) => (
                <li key={iso} className="ffx-date__closed-item">
                  <span className="ffx-date__closed-day">{parseIso(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                  <span className="ffx-date__closed-name">{nm}</span>
                  <span className="ffx-date__closed-tag">Closed</span>
                </li>
              ))}
            </ul>
          ) : null}
          {optional && selected ? (
            <div className="ffx-date__foot">
              <button type="button" className="ffx-date__clear" onClick={() => { onChange(""); close(); }}>Clear date</button>
            </div>
          ) : null}
        </div>
      ) : null;
  // The phone sheet and its scrim always go to <body>: a transformed/contained ancestor (cards, steppers) would
  // otherwise turn `position: fixed` into "fixed to that box" (half-width sheet, no scrim over the page).
  const layer = pp.present && sheet
    ? createPortal(<><div className="ffx-sheet-scrim" {...presenceAttrs(pp)} aria-hidden="true" onMouseDown={close} />{pop}</>, document.body)
    : pop && portal ? createPortal(pop, document.body) : pop;
  const hidden = name ? <input type="hidden" name={name} value={value ?? ""} /> : null;

  if (variant === "field" && readOnly) {
    return (
      <>
        <TextField id={baseId} label={label} readOnly value={selected ? fmtValue(selected) : ""} data-value={value ?? ""} hint={hint ?? undefined} error={error} className={cx("ffx-date--field is-readonly", className)} />
        {hidden}
      </>
    );
  }

  if (variant !== "chips") {
    const toolbar = variant === "toolbar";
    const trigId = `${baseId}-trigger`;
    const shown = selected ? fmtValue(selected) : "Choose…";
    const desc = [error ? `${baseId}-err` : hint ? `${baseId}-hint` : null].filter(Boolean).join(" ") || undefined;
    return (
      <div className={cx("ffc-field", toolbar ? "ffc-field--toolbar" : "ffc-field--select", "ffx-date", `ffx-date--${variant}`, selected && "is-filled", error ? "is-invalid" : null, className)} id={baseId}>
        <div className="ffc-box" ref={boxRef}>
          <button ref={triggerRef} id={trigId} type="button" className="ffc-trigger" aria-haspopup="dialog" aria-expanded={open}
            data-popup-open={open ? "" : undefined} data-value={value ?? ""}
            aria-label={toolbar ? `${label}: ${shown}` : undefined}
            aria-labelledby={toolbar ? undefined : `${baseId}-label ${baseId}-value`}
            aria-invalid={error ? true : undefined} aria-describedby={desc} onClick={toggle}>
            {toolbar ? (
              <span className="ffc-trigger__text">
                <span className="ffc-trigger__label">{label}</span>
                <span className="ffc-trigger__value">{shown}</span>
              </span>
            ) : (
              <span className="ffc-trigger__value" id={`${baseId}-value`}>{selected ? fmtValue(selected) : ""}</span>
            )}
            <span className="ffc-trigger__icon ffx-date__icon"><CalendarGlyph /></span>
          </button>
          {toolbar ? null : <label className="ffc-label" id={`${baseId}-label`} htmlFor={trigId}>{label}{optional ? <span className="ffc-optional"> Optional</span> : null}</label>}
        </div>
        {hidden}
        {toolbar ? null : (
          <>
            <p className="ffc-msg ffc-msg--error" id={`${baseId}-err`} aria-live="polite">{error || null}</p>
            {!error && hint ? <p className="ffc-msg" id={`${baseId}-hint`}>{hint}</p> : null}
          </>
        )}
        {layer}
      </div>
    );
  }

  return (
    <div className={cx("ffc-field ffx-date", className)} id={baseId}>
      <span className="ffx-date__label" id={`${baseId}-label`}>{label}</span>
      <div className="ffx-date__quick" role="group" aria-labelledby={`${baseId}-label`}>
        {quickChips()}
        <button ref={triggerRef} type="button" className="ffx-date__chip ffx-date__chip--more" aria-haspopup="dialog" aria-expanded={open}
          aria-describedby={`${baseId}-hint`} onClick={toggle}>
          <span className="ffx-date__chip-name">Other date</span>
          {selected && !quick.some((q) => isoDate(q) === value)
            ? <span className="ffx-date__chip-date">{fmtLong(selected)}</span>
            : <span className="ffx-date__chip-date is-empty">Choose…</span>}
          <CalendarGlyph />
        </button>
      </div>
      <span ref={hintRef} id={`${baseId}-hint`} className={cx("ffc-msg", pastCutoff && "ffx-date__hint--late")}>{hint}</span>
      {hidden}
      {layer}
    </div>
  );
}
